import { Check, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { PASSWORD_RULES, checkPassword, passwordStrength } from "@/lib/password";

interface PasswordRequirementsProps {
  value: string;
  /** Two columns fits wider forms such as the settings page. */
  columns?: 1 | 2;
  /** Hides the strength meter when the checklist alone is enough. */
  showStrength?: boolean;
  className?: string;
}

/**
 * Live checklist of the shared password rules plus a strength meter, so the
 * requirements are visible before the user submits instead of arriving as an
 * error afterwards.
 */
export function PasswordRequirements({
  value,
  columns = 1,
  showStrength = true,
  className,
}: PasswordRequirementsProps) {
  const results = checkPassword(value);
  const strength = passwordStrength(value);

  return (
    <div
      className={cn(
        "rounded-lg border border-border bg-muted/40 p-3.5",
        className,
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Requirements
        </span>
        {showStrength && (
          <span
            role="status"
            aria-live="polite"
            className={cn("text-[11px] font-semibold", strength.textClass)}
          >
            {value ? strength.label : ""}
          </span>
        )}
      </div>

      {showStrength && (
        <div className="mt-2 flex gap-1" aria-hidden>
          {PASSWORD_RULES.map((rule, i) => (
            <span
              key={rule.key}
              className={cn(
                "h-1 flex-1 rounded-full transition-colors duration-300",
                i < strength.score ? strength.barClass : "bg-border",
              )}
            />
          ))}
        </div>
      )}

      <ul
        className={cn(
          "mt-3 gap-x-4 gap-y-2",
          columns === 2 ? "grid sm:grid-cols-2" : "grid",
        )}
      >
        {results.map((rule) => (
          <li
            key={rule.key}
            className={cn(
              "flex items-center gap-2 text-xs transition-colors",
              rule.met ? "text-success" : "text-muted-foreground",
            )}
          >
            <span
              aria-hidden
              className={cn(
                "flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-colors",
                rule.met
                  ? "border-success bg-success text-success-foreground"
                  : "border-border bg-background",
              )}
            >
              {rule.met && <Check className="h-2.5 w-2.5" strokeWidth={3.5} />}
            </span>
            <span className={cn(rule.met && "font-medium")}>{rule.label}</span>
            <span className="sr-only">{rule.met ? "met" : "not met"}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Inline confirm-password feedback that updates as the user types. */
export function PasswordMatchHint({
  password,
  confirm,
  className,
}: {
  password: string;
  confirm: string;
  className?: string;
}) {
  if (!confirm) return null;
  const matches = password === confirm;

  return (
    <p
      role="status"
      aria-live="polite"
      className={cn(
        "flex items-center gap-1.5 text-xs",
        matches ? "text-success" : "text-destructive",
        className,
      )}
    >
      {matches ? (
        <Check className="h-3.5 w-3.5" />
      ) : (
        <X className="h-3.5 w-3.5" />
      )}
      {matches ? "Passwords match" : "Passwords don't match"}
    </p>
  );
}
